import type { ReactElement } from "react";
import { Box, Container, Grid, Paper, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import InsightsIcon from "@mui/icons-material/Insights";
import LinkIcon from "@mui/icons-material/Link";

interface Feature {
  icon: ReactElement;
  title: string;
  description: string;
}

const FEATURES: Feature[] = [
  {
    icon: <SearchIcon />,
    title: "AI Market Scanner",
    description: "Surface bullish and bearish opportunities across the market in seconds.",
  },
  {
    icon: <CandlestickChartIcon />,
    title: "Algo Strategies",
    description: "Deploy rule-based trading strategies directly through your connected broker.",
  },
  {
    icon: <AccountBalanceWalletIcon />,
    title: "Portfolio Tracker",
    description: "Track holdings, performance and risk in one consolidated view.",
  },
  {
    icon: <QueryStatsIcon />,
    title: "Open Interest Engine",
    description: "Institutional-grade option chain and open interest analytics.",
  },
  {
    icon: <InsightsIcon />,
    title: "Real-Time Analytics",
    description: "Live market intelligence, sentiment and regime signals as they happen.",
  },
  {
    icon: <LinkIcon />,
    title: "Secure Broker Connectivity",
    description: "Encrypted, official API connections to Dhan, Zerodha and ICICI Direct.",
  },
];

export function FeatureCardsSection() {
  return (
    <Box sx={{ py: { xs: 10, md: 14 } }} id="features">
      <Container maxWidth="lg">
        <Box textAlign="center" maxWidth={640} mx="auto" mb={8}>
          <Typography variant="h4" fontWeight={800} mb={2}>
            Everything a modern trading desk needs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            One platform for research, execution and portfolio oversight — built on top of the
            broker you already use.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {FEATURES.map((feature) => (
            <Grid item xs={12} sm={6} md={4} key={feature.title}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3.5,
                  height: "100%",
                  borderRadius: 3,
                  transition: "box-shadow .2s, transform .2s",
                  "&:hover": { boxShadow: 4, transform: "translateY(-2px)" },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2.5,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h6" fontWeight={700} mb={1}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default FeatureCardsSection;
